import styled from "styled-components";

import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { StatusTone } from "@pages/AdminDashboard/ui/StatusBadge/statusTone";
import { ToneBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { MedicationInclusion, OrderMedication, OrderMedicationsState } from "@types-runway/admin";

const { color, radius } = adminTokens;
const [, spaceSm, spaceMd, , spaceLg] = adminTokens.spacing;

const Box = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  overflow: hidden;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spaceSm}px ${spaceLg}px;
  padding: ${spaceMd - 2}px ${spaceLg - 2}px;

  & + & {
    border-top: 1px solid ${color.border};
  }
`;

const Text = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Name = styled.em`
  font-family: ${adminFontFamily.mediumItalic};
  font-style: italic;
`;

const Directions = styled.span`
  color: ${color.inkSoft};
`;

const Status = styled.span`
  color: ${color.inkSoft};
`;

// Each mark's words and look (MEDS-R2…R5).
const marks: Record<MedicationInclusion, { label: string; tone: StatusTone }> = {
  included: { label: "Included", tone: "done" },
  not_included: { label: "Not included", tone: "inactive" },
  not_sent: { label: "Not sent yet", tone: "needsAttention" },
  unknown: { label: "Unknown", tone: "inactive" },
};

const Medication = ({ medication }: { medication: OrderMedication }) => {
  const mark = marks[medication.inclusion];

  return (
    <Row aria-label={`${medication.name}, ${mark.label}`}>
      <Text>
        <Name>{medication.name}</Name>
        <Directions>{medication.directions}</Directions>
      </Text>
      <ToneBadge tone={mark.tone}>{mark.label}</ToneBadge>
    </Row>
  );
};

interface MedicationListProps {
  // Absent until the Order's medications are asked for.
  medications?: OrderMedicationsState;
}

// An Order's medications, each with its name, its directions and what the Order sent
// (MEDS-R1…R7).
export const MedicationList = ({ medications }: MedicationListProps) => {
  if (!medications || medications.status === "pending") {
    return <Status>Loading medications…</Status>;
  }
  if (medications.status === "failed") {
    return <Status>The medications couldn&apos;t be loaded.</Status>;
  }
  if (medications.medications.length === 0) {
    return <Status>No medications on this Order.</Status>;
  }

  return (
    <Box>
      {medications.medications.map((medication) => (
        <Medication key={medication.caseOfferingId} medication={medication} />
      ))}
    </Box>
  );
};
